/*
 * Copyright (c) 2023 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import {Injectable} from "@angular/core";
import {AbstractRoleService, RoleService, RoleWithRightsDto, RoleWithRightsVo} from "@irian-ro/auth-web-client";
import {Observable, switchMap, take} from "rxjs";
import {JsonApiResource} from "@irian-ro/abstract-resource";
import {RoleCompanyTypeResourceService} from "@well/resource-services";
import {LoggedInContext, WellContextService} from "@well/shared-providers";
import {RoleCompanyTypeMeta} from "@well/domain";

@Injectable({providedIn: 'root'})
export class RoleWithCompanyTypeService implements AbstractRoleService{

  constructor(private roleCompanyTypeResourceService: RoleCompanyTypeResourceService,
              private roleService: RoleService,
              private wellContextService: WellContextService) {
  }

  findAll(): Observable<JsonApiResource<RoleWithRightsVo[], any>>{
    return this.wellContextService.getLoggedInContext().pipe(
      take(1),
      switchMap((loggedInContext: LoggedInContext) => {
        return this.roleCompanyTypeResourceService.getRolesByCompanyType(loggedInContext.companyType)
      }));
  }

  findOne(roleUuid: string): Observable<JsonApiResource<RoleWithRightsDto, RoleCompanyTypeMeta>>{
    return this.wellContextService.getLoggedInContext().pipe(
      take(1),
      switchMap((loggedInContext: LoggedInContext) => {
        return this.roleCompanyTypeResourceService.getRoleDetailsByCompanyType(roleUuid, loggedInContext.companyType)
      })
    );
  }

  findRolesByUserUuid(userUuid: string): Observable<JsonApiResource<RoleWithRightsVo[], any>> {
    return this.wellContextService.getLoggedInContext().pipe(
      take(1),
      switchMap((loggedInContext: LoggedInContext) => {
        return this.roleCompanyTypeResourceService.getUserRolesByCompanyType(userUuid, loggedInContext.companyType)
      })
    );
  }

  getEditedRoleDto(operation: string, resourceUuid: string): Observable<JsonApiResource<RoleWithRightsDto, any>> {
    switch (operation){
      case "new":{
        return this.getNewTemplate();
      }

      case "duplicate":{
        return this.getNewTemplateFromOrigin(resourceUuid);
      }

      case "edit": {
        return this.findOne(resourceUuid);
      }
      default: throw new Error("Operation not supported");
    }
  }

  getNewTemplate(): Observable<JsonApiResource<RoleWithRightsDto, any>> {
    return this.wellContextService.getLoggedInContext().pipe(
      take(1),
      switchMap((loggedInContext: LoggedInContext) => {
        return this.roleCompanyTypeResourceService.getTemplateByCompanyType(loggedInContext.companyType)
      })
    );
  }

  getNewTemplateFromOrigin(originalRoleUuid: string): Observable<JsonApiResource<RoleWithRightsDto, any>> {
    return this.wellContextService.getLoggedInContext().pipe(
      take(1),
      switchMap((loggedInContext: LoggedInContext) => {
        return this.roleCompanyTypeResourceService.getTemplateFromOriginRoleByCompanyType(originalRoleUuid, loggedInContext.companyType)
      }),
    );
  }

  remove(uuid: string): Observable<void> {
    return this.roleService.remove(uuid);
  }

  save(roleWithRightsDto: RoleWithRightsDto): Observable<JsonApiResource<RoleWithRightsDto, any>> {
    return this.roleCompanyTypeResourceService.saveRoleByCompanyType(roleWithRightsDto);
  }

  update(roleWithRightsDto: RoleWithRightsDto): Observable<JsonApiResource<RoleWithRightsDto, any>> {
    return this.roleCompanyTypeResourceService.updateRole(roleWithRightsDto);
  }

  findRolesForNewUser(): Observable<JsonApiResource<RoleWithRightsVo[], any>>{
    return this.wellContextService.getLoggedInContext().pipe(
      take(1),
      switchMap((loggedInContext: LoggedInContext) => {
        return this.roleCompanyTypeResourceService.getRolesForNewUserByCompanyType(loggedInContext.companyType)
      }),
    );
  }

}
